import React, { createContext, useContext, useEffect, useReducer, ReactNode, useCallback } from 'react';
import {
  PicksState,
  PicksAction,
  Pick,
  PickSelections,
  Product,
  ProductVariant,
  SharedPickResolution,
} from '../types';
import { makePickId, MAX_PICKS } from '../utils/picksShare';

/**
 * Is this pick "complete" — meaning it has enough info to add directly to cart?
 * (a variant resolved + a color in selections). Anything less is "bare" and
 * the user should be asked to configure it first.
 */
export function isPickComplete(p: Pick): boolean {
  return !!(p.variant && p.selections.color);
}

const STORAGE_KEY = 'ndms_picks_v1';

const CTX = createContext<{
  state: PicksState;
  dispatch: React.Dispatch<PicksAction>;
  addPick: (product: Product, variant: ProductVariant | null, selections: PickSelections) => boolean;
  removePick: (pickId: string) => void;
  togglePick: (product: Product, variant: ProductVariant | null, selections: PickSelections) => 'added' | 'removed' | 'full';
  clearPicks: () => void;
  openPicks: () => void;
  closePicks: () => void;
  hasPick: (productId: string, selections: PickSelections) => boolean;
  findPick: (productId: string, selections: PickSelections) => Pick | undefined;
  totalPicks: number;
  canAddMore: boolean;
  // Configure flow — ask product modal to open with prefill
  requestConfigure: (product: Product, prefill: PickSelections, fromPickId?: string) => void;
  clearPendingConfig: () => void;
  // Shared-picks API
  setSharedPicks: (items: SharedPickResolution[], isReorder?: boolean) => void;
  openSharedModal: () => void;
  closeSharedModal: () => void;
  dismissSharedBanner: () => void;
  clearShared: () => void;
} | null>(null);

function sameSelections(a: PickSelections, b: PickSelections): boolean {
  return (
    (a.color || '') === (b.color || '') &&
    (a.compressionLevel || '') === (b.compressionLevel || '') &&
    (a.style || '') === (b.style || '') &&
    (a.size || '') === (b.size || '') &&
    (a.gender || '') === (b.gender || '')
  );
}

function picksReducer(state: PicksState, action: PicksAction): PicksState {
  switch (action.type) {
    case 'HYDRATE':
      return { ...state, picks: action.picks.slice(0, MAX_PICKS), hydrated: true };
    case 'ADD_PICK': {
      const dup = state.picks.find(
        (p) => p.product.productId === action.product.productId && sameSelections(p.selections, action.selections)
      );
      if (dup) return state;
      if (state.picks.length >= MAX_PICKS) return state;
      const pick: Pick = {
        pickId: makePickId(),
        product: action.product,
        variant: action.variant,
        selections: action.selections,
        addedAt: Date.now(),
      };
      return { ...state, picks: [...state.picks, pick] };
    }
    case 'REMOVE_PICK':
      return { ...state, picks: state.picks.filter((p) => p.pickId !== action.pickId) };
    case 'CLEAR_PICKS':
      return { ...state, picks: [] };
    case 'OPEN_PICKS':
      return { ...state, isOpen: true };
    case 'CLOSE_PICKS':
      return { ...state, isOpen: false };
    case 'TOGGLE_PICKS':
      return { ...state, isOpen: !state.isOpen };
    case 'SET_SHARED':
      return { ...state, shared: { showBanner: true, showModal: false, items: action.items, isReorder: action.isReorder } };
    case 'DISMISS_SHARED_BANNER':
      return state.shared ? { ...state, shared: { ...state.shared, showBanner: false } } : state;
    case 'OPEN_SHARED_MODAL':
      return state.shared ? { ...state, shared: { ...state.shared, showModal: true } } : state;
    case 'CLOSE_SHARED_MODAL':
      return state.shared ? { ...state, shared: { ...state.shared, showModal: false } } : state;
    case 'CLEAR_SHARED':
      return { ...state, shared: null };
    case 'REQUEST_CONFIGURE':
      return {
        ...state,
        pendingConfig: {
          product: action.product,
          prefill: action.prefill,
          fromPickId: action.fromPickId,
        },
      };
    case 'CLEAR_PENDING_CONFIG':
      return { ...state, pendingConfig: null };
    default:
      return state;
  }
}

const INITIAL_STATE: PicksState = {
  picks: [],
  isOpen: false,
  hydrated: false,
  shared: null,
  pendingConfig: null,
};

export function PicksProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(picksReducer, INITIAL_STATE);

  // Hydrate from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          dispatch({ type: 'HYDRATE', picks: parsed as Pick[] });
          return;
        }
      }
    } catch {
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    }
    dispatch({ type: 'HYDRATE', picks: [] });
  }, []);

  // Persist picks whenever they change (after hydration)
  useEffect(() => {
    if (!state.hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state.picks));
    } catch {
      // Safari private mode, quota exceeded, etc. — silently degrade
    }
  }, [state.picks, state.hydrated]);

  const addPick = useCallback(
    (product: Product, variant: ProductVariant | null, selections: PickSelections): boolean => {
      if (state.picks.length >= MAX_PICKS) return false;
      dispatch({ type: 'ADD_PICK', product, variant, selections });
      return true;
    },
    [state.picks.length]
  );

  const removePick = useCallback((pickId: string) => dispatch({ type: 'REMOVE_PICK', pickId }), []);

  const findPick = useCallback(
    (productId: string, selections: PickSelections) =>
      state.picks.find(
        (p) => p.product.productId === productId && sameSelections(p.selections, selections)
      ),
    [state.picks]
  );

  const hasPick = useCallback(
    (productId: string, selections: PickSelections) => !!findPick(productId, selections),
    [findPick]
  );

  const togglePick = useCallback(
    (product: Product, variant: ProductVariant | null, selections: PickSelections): 'added' | 'removed' | 'full' => {
      const existing = findPick(product.productId, selections);
      if (existing) {
        dispatch({ type: 'REMOVE_PICK', pickId: existing.pickId });
        return 'removed';
      }
      if (state.picks.length >= MAX_PICKS) return 'full';
      dispatch({ type: 'ADD_PICK', product, variant, selections });
      return 'added';
    },
    [findPick, state.picks.length]
  );

  const clearPicks = useCallback(() => dispatch({ type: 'CLEAR_PICKS' }), []);
  const openPicks = useCallback(() => dispatch({ type: 'OPEN_PICKS' }), []);
  const closePicks = useCallback(() => dispatch({ type: 'CLOSE_PICKS' }), []);

  const requestConfigure = useCallback(
    (product: Product, prefill: PickSelections, fromPickId?: string) =>
      dispatch({ type: 'REQUEST_CONFIGURE', product, prefill, fromPickId }),
    []
  );
  const clearPendingConfig = useCallback(() => dispatch({ type: 'CLEAR_PENDING_CONFIG' }), []);

  const setSharedPicks = useCallback((items: SharedPickResolution[], isReorder?: boolean) => {
    if (items.length === 0) return;
    dispatch({ type: 'SET_SHARED', items, isReorder });
  }, []);
  const openSharedModal = useCallback(() => dispatch({ type: 'OPEN_SHARED_MODAL' }), []);
  const closeSharedModal = useCallback(() => dispatch({ type: 'CLOSE_SHARED_MODAL' }), []);
  const dismissSharedBanner = useCallback(() => dispatch({ type: 'DISMISS_SHARED_BANNER' }), []);
  const clearShared = useCallback(() => dispatch({ type: 'CLEAR_SHARED' }), []);

  const totalPicks = state.picks.length;
  const canAddMore = totalPicks < MAX_PICKS;

  return (
    <CTX.Provider
      value={{
        state,
        dispatch,
        addPick,
        removePick,
        togglePick,
        clearPicks,
        openPicks,
        closePicks,
        hasPick,
        findPick,
        totalPicks,
        canAddMore,
        requestConfigure,
        clearPendingConfig,
        setSharedPicks,
        openSharedModal,
        closeSharedModal,
        dismissSharedBanner,
        clearShared,
      }}
    >
      {children}
    </CTX.Provider>
  );
}

export function usePicks() {
  const ctx = useContext(CTX);
  if (!ctx) throw new Error('usePicks must be used inside PicksProvider');
  return ctx;
}
